import { Component } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LicenseArea } from 'src/app/data-models/area-models/license-area.model';
import { ManagementArea } from 'src/app/data-models/area-models/management-area.model';
import { Logbook } from 'src/app/data-models/user-models/logbook.model';
import { SubmissionService } from 'src/app/services/reporting/submission.service';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { CommonDataService } from 'src/app/services/storage/common-data.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { Trip } from '../../data-models/trip-models/trip.model';
import { UserData } from '../../data-models/user-models/user-data.model';
import { VesselData } from '../../data-models/user-models/vessel-data.model';
import { AuthenticationService } from '../../services/authentication.service';
import { StartReport } from '../../data-models/report-models/start-report.model';
import { environment } from '../../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { FinData, FinDialogComponent } from '../../dialogs/fin-dialog/fin-dialog.component';
import { MatSelectChange } from '@angular/material/select';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';


export interface FinOption {
  name: string;
  fin: string;
}


@Component({
    selector: 'app-start-report',
    templateUrl: './start-report.component.html',
    styleUrls: ['./start-report.component.css'],
    standalone: false
})
export class StartReportComponent {

  isProduction: boolean = environment.production;

  isReportSending: boolean = false; // flag that tells us if the report is sending (so we can show the loading screen)

  // data preambles
  currTrip: Trip = new Trip();
  lastTrip: Trip = new Trip();
  startReport: StartReport = new StartReport();
  userDetails: UserData = new UserData();
  logbooks: Logbook[] = [];
  userVessels: VesselData[] = [];
  licenseAreas: LicenseArea[] = [];
  emptyLogbook: Logbook = new Logbook();
  emptyVessel: VesselData = new VesselData();
  emptyArea: LicenseArea = new LicenseArea();
  managementAreas: ManagementArea[] = [];
  finOptions: FinOption[] = [];

  // Sentinel for “Add new fisher...” special option
  ADD_NEW_FISHER: string = '__ADD_FISHER__';

  // Track whatever the last valid FIN was
  oldFinValue: string | null = null;


  // currently selected logbooks and license areas
  selectedLogbook: Logbook = new Logbook();
  selectedVessel: VesselData = new VesselData();
  selectedLicenseArea: LicenseArea = new LicenseArea();

  // validation check for summary page
  logbookValid: boolean = false;
  vesselValid: boolean = false;
  licenseAreaValid: boolean = false;
  mgmtAreaValid: boolean = false;
  startTimeValid: boolean = false;
  
  // report data
  startForm: UntypedFormGroup = new UntypedFormGroup({
    fin: new FormControl<string>('', [Validators.required]),
    vesselMasterAgreement: new FormControl<boolean>(false, [Validators.requiredTrue]),
    logbook: new FormControl<Logbook>(this.emptyLogbook, [Validators.required]),
    licenseArea: new FormControl<LicenseArea>(this.emptyArea, [Validators.required]),
    managementArea: new FormControl<string>('', [Validators.required]),
    startTime: new FormControl<{ hour: number, minute: number }>(null!, [Validators.required]),
    comments: new FormControl<string>('')
  });

  startDate: Date = new Date();

  reportStatus: string = '';
  isComplete: boolean = false;
  reportFailed: boolean = false;
  isResubmission: boolean = false;
  
  // page to page
  nextButtonAttempt: boolean = false;
  numPages: number = 0;
  currPage: number = 0;
  spinnerProgress: number = 0;
  spinnerIncrement: number = 0;

  // get the needed data from the user pouchdb and common couchdb
  constructor(
    private router: Router,
    public route: ActivatedRoute,
    private submissionService: SubmissionService,
    private authenticationService: AuthenticationService,
    public commonDataService: CommonDataService,
    public sharedMethodsService: SharedMethodsService,
    private dialog: MatDialog,
  ) {

    // array of documents needed from the common data pouch for this component
    let requiredData: string[] = ['license_areas_list', 'management_areas_list'];

    // check if we are editing a report
    // if so, we will use the trip and report data passed from the records page
    this.reportStatus = this.route.snapshot.queryParams.status;

    this.authenticationService.getUserDetails().then(() => {
      this.userDetails = UserStorageService.currUser;
    }).then(() => {
      UserStorageService.getLastTrip().then(() => {
        if (this.authenticationService.dataError(UserStorageService.error)) {
          return;
        }
        this.lastTrip = UserStorageService.lastTrip;
        if (this.reportStatus == 'complete') {
          this.currTrip = UserStorageService.currTrip;
          this.startReport = this.currTrip.startFishingReport
        }
        commonDataService.getSpecifiedDocs(this.userDetails.userEmail, requiredData).then(() => {
          this.initialize();
        });
      });
    });

  }


  /*  ---------- Report Initialization Methods  ---------- */


  // initialize the data
  initialize(): void {

    // get all logbooks, vessels, and license areas
    this.logbooks = this.userDetails.logbooks;
    this.userVessels = this.userDetails.vessels;
    this.licenseAreas = CommonDataService.licenseAreas;

    // Load FIN options from the user storage
    UserStorageService.getFinOptions()
      .then((options: FinOption[]) => {
        this.finOptions = options;
        // If no value default to the vessel master of the last trip.
        const currentValue = this.startForm.get('fin')?.value;
        if (!currentValue) {
          const defaultFin: string | undefined = this.getDefaultFin();
          if (defaultFin) {
            this.startForm.get('fin')?.setValue(defaultFin);
            this.oldFinValue = defaultFin;
          }
        }
      })
      .catch((error) => {
        console.error("Error loading FIN options:", error);
      });


    // initialize form 
    let currLogbook: Logbook = this.emptyLogbook;
    let currArea: LicenseArea = this.emptyArea;
    let currManagementAreaIndex: number = 0;

    // the last trip's logbook may have been deactivated since (season rollover), so only
    // pre-select it if it is still in the selectable list shown on page 1
    const lastTripLogbook: Logbook | undefined = this.lastTrip && this.lastTrip.isValid()
      ? this.logbooks?.find((logbook: Logbook) => logbook.logbookID === this.lastTrip.logbook?.logbookID && logbook.isRegistered && logbook.isActive)
      : undefined;

    if (lastTripLogbook) { // populate form data with data from the last trip

      currLogbook = this.setSelectedLogbook(lastTripLogbook);
      currArea = this.setSelectedArea(this.lastTrip.licenseArea ?? new LicenseArea());

    }

    else { // populate some form data 

      currLogbook = this.logbooks?.find((logbook: Logbook) => logbook.isRegistered && logbook.isActive) ?? this.emptyLogbook;
      currLogbook.isChecked = true;

      let licenseIndex: number = 0;
      if (currLogbook.gearName === 'Gill Net') {
        licenseIndex = 2; // set to License Area C
      } else if (currLogbook.gearName === 'Troll') {
        licenseIndex = 5; // set to License Area F
      }

      currArea = this.setSelectedArea(this.licenseAreas[licenseIndex]);
      this.startForm.controls['licenseArea'].setValue(currArea);

    }

    // populate report
    this.selectedLogbook = currLogbook;
    this.selectedVessel = currLogbook.vessel;
    this.selectedLicenseArea = currArea;
    // get management areas based on the currently selected license area
    this.managementAreas = this.sharedMethodsService.getManagementAreas(this.selectedLicenseArea.licenseName, CommonDataService.managementAreas);
    currManagementAreaIndex = this.sharedMethodsService.getLastManagementArea(this.lastTrip.startFishingReport.managementArea, this.managementAreas);

    if (this.reportStatus == 'complete') {

      if (SharedMethodsService.getReportStatus(this.startReport.confirmationNumber) == 'Error with submission') {
        this.reportFailed = true;
      }
      this.isComplete = true;
      this.patchForm();
      this.setSummaryPage();
      this.numPages = -1;
      this.currPage = 0;
      this.spinnerProgress = 100;
      this.spinnerIncrement = 100 / this.numPages;

    }

    else {

      this.startForm.patchValue({
        logbook: currLogbook,
        licenseArea: currArea,
        managementArea: this.managementAreas[currManagementAreaIndex],
      });

      this.numPages = 4;
      this.spinnerIncrement = 100 / this.numPages;
      this.toNextPage();
    }

  }

  ngOnInit(): void {

    this.updateManagementAreas();

  }

  
  patchForm() {

    this.selectedLicenseArea = this.currTrip.licenseArea;
    this.selectedLogbook = this.currTrip.logbook;
    this.selectedVessel = this.currTrip.vessel;
    this.startDate = this.startReport.startDate;

    this.startForm.patchValue({
      fin: this.currTrip.fin,
      vesselMasterAgreement: this.startReport.vesselMasterAgreement,
      logbook: this.selectedLogbook,
      licenseArea: this.selectedLicenseArea,
      managementArea: this.startReport.managementArea,
      startTime: {hour: this.startDate.getHours(), minute: this.startDate.getMinutes()},
      comments: this.startReport.comments,
    });
    
  }

  // Reopens the report, only possible if a submitted report gets a failure response from FOS
  reopenReport() {

    this.currPage = 1;
    this.numPages = 4;
    this.spinnerIncrement = 100 / this.numPages;
    this.isComplete = false;
    this.reportFailed = false;
    this.spinnerProgress = this.spinnerIncrement;
    this.isResubmission = true;

  }


  compareArea(a1: ManagementArea, a2: ManagementArea): boolean {
    return a1 && a2 ? a1.name == a2.name && a1.ID === a2.ID : a1 === a2;
  }



  /* ---------- Page Navigation Methods ---------- */


  // check current page before going to the next one
  toNextPage(): void {

    // page is summary page
    if (this.currPage > this.numPages) {
      this.dialog.open(ConfirmationDialogComponent, {
        data: { screen: 'vessel_master_confirm', vesselMasterName: this.selectedFinOption?.name }
      }).afterClosed().subscribe(result => {
        if (result) {
          this.submitReport();
        } else {
          // NO: reset back to page 1 so they can re-pick or add a Vessel Master
          this.currPage = 1;
          this.spinnerProgress = this.spinnerIncrement;
        }
      });
      return;
    }

    switch (this.currPage) {

      // save selected logbook
      case 1:
        this.saveLogbook();
        if (!this.startForm.get('vesselMasterAgreement')!.valid) {
          this.sharedMethodsService.toastInvalidForm();
        } else {
          this.incrementPage();
        }
        return;

      // save selected license area
      case 2:
        this.saveLicenseArea();
        if (!this.startForm.get('managementArea')!.valid) {
          if (!this.nextButtonAttempt) {
            this.sharedMethodsService.toastInvalidForm();
            this.nextButtonAttempt = true;
          } else {
            this.nextButtonAttempt = false;
            this.incrementPage();

          }
          return;
        }        
        break;

      case 3:
        const ctrl = this.startForm.get('startTime')!;
        if (!ctrl.valid) {
          if (!this.nextButtonAttempt) {
            this.sharedMethodsService.toastInvalidForm();
            this.nextButtonAttempt = true;
          } else {
            this.nextButtonAttempt = false;
            this.incrementPage();

          }
          return;
        }
        const { hour, minute } = ctrl.value;
        this.sharedMethodsService.setDateTime(this.startDate, { hour, minute });
        break;

      // set the summary page
      case 4:
        this.setSummaryPage(); // set the summary page
        break;

    }

    this.incrementPage();

    if (this.currPage == 2 || this.currPage == 3) {
      this.nextButtonAttempt = false;
    }

  }

  // forward one page
  incrementPage(): void {

    this.currPage++;
    this.spinnerProgress += this.spinnerIncrement;

  }

  // back one page
  toPreviousPage(): void {

    switch (this.currPage) {

      // back to home if in summary read only report
      case 0:
        this.sharedMethodsService.toHome(this.userDetails.currHomepage);
        break;
      // back to home if at beginning of report
      case 1:
        this.sharedMethodsService.toHome(this.userDetails.currHomepage);
        break;

       // save selected license area
      case 2:
        this.saveLicenseArea();
        break;

    }

    this.currPage--;
    this.spinnerProgress -= this.spinnerIncrement;

  }

  // set all of the data to display on the summary based on the entered report fields
  setSummaryPage(): void {

    // set validation values
    this.logbookValid = this.selectedLogbook && this.selectedLogbook.isValid();
    this.vesselValid = this.selectedVessel && this.selectedVessel.isValid();
    this.licenseAreaValid = this.selectedLicenseArea && this.selectedLicenseArea.isValid();
    this.mgmtAreaValid = !this.startForm.get('managementArea')?.errors?.required;
    this.startTimeValid = this.startForm.get('startTime')?.valid ?? false;

  }


  /* ---------- Helper Methods ---------- */


  get selectedFinOption(): FinOption | undefined {
    const selectedFin: string = String(this.startForm.get('fin')?.value ?? '');
    return this.finOptions.find(option => String(option.fin) === selectedFin);
  }


  // the FIN to pre-select on a new start report: the vessel master used on the last trip, as long as
  // that FIN is still one of the user's saved options, otherwise fall back to the first saved option
  getDefaultFin(): string | undefined {

    // trips saved before FINs were normalized may hold a number here, so compare as strings
    const lastFin: string = String(this.lastTrip?.fin ?? '');

    const lastOption: FinOption | undefined = this.finOptions.find((option: FinOption) => String(option.fin) === lastFin);
    if (lastFin && lastOption) {
      return lastOption.fin;
    }

    return this.finOptions[0]?.fin;

  }


  onFinChange(event: MatSelectChange): void {
    const newValue = event.value;

    if (newValue === this.ADD_NEW_FISHER) {
      event.source.writeValue(this.oldFinValue);
      this.openNewFinDialog(this.oldFinValue);
    } else {
      this.oldFinValue = newValue;
    }
  }


  openNewFinDialog(previousValue: string | null): void {
    const dialogRef = this.dialog.open(FinDialogComponent, {
      width: '350px',
      data: { name: '', fin: '' } as FinData
    });

    dialogRef.afterClosed().subscribe((result: FinData | undefined) => {
      if (result && result.name && result.fin) {
        const newFin: string = String(result.fin);
        // Check if the FIN already exists
        if (this.finOptions.some(option => String(option.fin) === newFin)) {
          this.sharedMethodsService.toastFINUniqueError();
          this.startForm.get('fin')?.setValue(previousValue);
          return;
        }
        const newOption: FinOption = { name: result.name, fin: newFin };
        this.finOptions.push(newOption);

        UserStorageService.saveFinOptions(this.finOptions)
          .then(() => {
            // Update the form control to store the NEW fin
            this.startForm.get('fin')?.setValue(newFin);
            // Also remember it as "oldFinValue" for next time
            this.oldFinValue = newFin;
          })
          .catch(error => console.error("Error saving new FIN option:", error));
      } else {
        // Dialog canceled; revert to the old FIN
        this.startForm.get('fin')?.setValue(previousValue);
      }
    });
  }




  // set the radio buttons to the currently selected logbook in the fishing report
  setSelectedLogbook(currLogbook: Logbook): Logbook {

    for (let logbook of this.logbooks) {
      logbook.isChecked = (logbook.logbookID === currLogbook.logbookID);
    }
    return currLogbook;

  }

  determineAreaClass(area: LicenseArea): string {

    if (area.gearType !== this.selectedLogbook.gearName) {
      return 'hide';
    }
      return '';

  }

  // set the radio buttons to the currently selected vessel in the fishing report
  setSelectedArea(currArea: LicenseArea): LicenseArea {

    for (let licenseArea of this.licenseAreas) {
      licenseArea.isChecked = (licenseArea.licenseName === currArea.licenseName);
    }
    return currArea;

  }

  // save the selected logbook for UI
  saveLogbook(): void {

    // change license area list if selected logbook has changed
    let thisLogbook: Logbook = this.startForm.get('logbook')?.value;
    if (!(this.selectedLogbook.gearName === thisLogbook.gearName && this.selectedLogbook.logbookID === thisLogbook.logbookID)) {
      let licenseIndex: number = 0;
      if (thisLogbook.gearName === 'Gill Net') {
        licenseIndex = 2;
      } else if (thisLogbook.gearName === 'Troll') {
        licenseIndex = 5;
      }
      this.startForm.controls['licenseArea'].setValue(this.setSelectedArea(this.licenseAreas[licenseIndex]));
    }

    // a stale form value must not resolve to a deactivated logbook
    this.selectedLogbook = this.logbooks.find((logbook: Logbook) =>
      logbook.logbookID === thisLogbook.logbookID && logbook.isActive && logbook.isRegistered
    ) ?? this.emptyLogbook;
    this.selectedVessel = this.selectedLogbook.vessel;
    this.setSelectedLogbook(this.selectedLogbook);
    this.startForm.patchValue({
      logbook: this.selectedLogbook
    });

  }

  // save the selected license area for UI
  saveLicenseArea(): void {

    this.selectedLicenseArea = LicenseArea.transformAnonymous(this.startForm.get('licenseArea')?.value);
    this.setSelectedArea(this.selectedLicenseArea);
    this.startForm.patchValue({
      licenseArea: this.selectedLicenseArea
    });

  }

  // compares two lists of management areas and determines if they are equal
  compareAreaLists(oldAreaList: ManagementArea[], currAreaList: ManagementArea[]): boolean {

    if (oldAreaList.length !== currAreaList.length) {
      return false;
    }

    for (let i: number = 0; i < oldAreaList.length; i++) {
      if (oldAreaList[i].name !== currAreaList[i].name) {
        return false;
      }
    }

    return true; // return true if equal

  }

  // updates the list of management areas and removes any previously selected areas in the form if needed
  updateManagementAreas(): void {

    this.startForm.get('licenseArea')?.valueChanges.subscribe((value: any) => {

      let oldAreaList: ManagementArea[] = this.managementAreas;
      this.managementAreas = this.sharedMethodsService.getManagementAreas(value.licenseName, CommonDataService.managementAreas);
      // check if the management area list has changed
      if (!this.compareAreaLists(oldAreaList, this.managementAreas)) {
        this.startForm.controls['managementArea'].reset(); // if so, clear the form value
       }

    });

  }

  // returns the current date with the current start fishing time, the returned date should only be used to read time
  getStartTime(): Date {

    const startTimeControl = (this.startForm.get('startTime') as FormControl);
    let tempDate: Date = new Date();
    if (startTimeControl) {

      const { hour, minute } = startTimeControl.value;
      tempDate.setHours(hour, minute, 0, 0);
      
      return tempDate;
    } else {
      return tempDate;
    }

  }

  fillCurrentTime() {
    const ctrl = this.startForm.get('startTime')!;
    const now = new Date();
    ctrl.setValue({ hour: now.getHours(), minute: now.getMinutes() });
    ctrl.markAsDirty(); 
  }

  // Custom validator to restrict startTime to be within 6 hours of the current time (national requirement)

  startTimeValidator(): ValidatorFn {

  return (control: AbstractControl): ValidationErrors | null => {
    const startTime = control.value as { hour: number, minute: number };

    if (startTime) {
      const currentDateTime = new Date();
      const selectedDateTime = new Date();
      selectedDateTime.setHours(startTime.hour, startTime.minute, 0, 0);

      const timeDifferenceInMilliseconds = selectedDateTime.getTime() - currentDateTime.getTime();
      const maxAllowedDifference = 6 * 60 * 60 * 1000; // 6 hours in milliseconds

      if (timeDifferenceInMilliseconds > maxAllowedDifference) {
        return { withinSixHours: true }; // Validation error if more than 6 hours in the future
      }
    }

    return null; // Validation passed
  };
}


  /* ---------- Report Completion Methods ---------- */


  isReportValid(): boolean {

    return this.startForm.valid && 
    this.logbookValid && 
    this.vesselValid && 
    this.licenseAreaValid && 
    this.startTimeValid;

  }


  // send start fishing report to DFO
  submitReport(): void {

    if (this.isReportValid()) {
      this.isReportSending = true;
      this.saveCurrTrip(); // update the user's active trip
      this.updateHomepage(); // update the user's active homepage
      this.saveReportData(); // update the user's start fishing report
      this.attemptTransmission(); // attempt to send the report to DFO
    }
    else {
      this.sharedMethodsService.toastInvalidForm();
    }

  }

  // save the current trip information
  saveCurrTrip(): void {
    this.currTrip.fin = String(this.startForm.get('fin')?.value ?? '');
    this.currTrip.logbook = Logbook.transformAnonymous(this.startForm.get('logbook')?.value);
    this.currTrip.vessel = this.currTrip.logbook.vessel;
    this.currTrip.licenseArea = LicenseArea.transformAnonymous(this.startForm.get('licenseArea')?.value);
    this.currTrip.offloadNeeded = this.determineOffloadReport();

  }

  // save the start fishing report information
  saveReportData(): void {

    const startTimeControl = (this.startForm.get('startTime') as FormControl);

    if (startTimeControl) {

      const { hour, minute } = startTimeControl.value;

      this.currTrip.startFishingReport.isComplete = true;
      this.sharedMethodsService.setDateTime(this.startDate, { hour, minute }); // add the start time to the start date
      this.currTrip.startFishingReport.startDate = this.startDate;
      this.currTrip.startFishingReport.managementArea = ManagementArea.transformAnonymous(this.startForm.get('managementArea')?.value);
      this.currTrip.startFishingReport.completionDate = new Date(); // get the current date
      this.currTrip.startFishingReport.submissionDate = new Date();
      this.currTrip.startFishingReport.comments = this.startForm.get('comments')?.value.trim();
      this.currTrip.startFishingReport.isComplete = true;
      this.currTrip.startFishingReport.vesselMasterAgreement = this.startForm.get('vesselMasterAgreement')?.value ?? false;

    }

  }

  // sets flag in trip data if this trip may need an offload report
  determineOffloadReport(): boolean {

    // we only need an offload report if the trip is Seine in Area B, or Gill Net in Area C, D, or, E
    let licenseName: string = this.currTrip.licenseArea.licenseName;
    let gearName: string = this.currTrip.logbook.gearName;
    let isSeineOffload: boolean = gearName === 'Seine' && licenseName === 'B';
    let isGillOffload: boolean = gearName === 'Gill Net';
    return isSeineOffload || isGillOffload;

  }

  updateHomepage(): void {

    this.userDetails.currHomepage = 'active-trip-home';

  }

  // composes the email body and subject, then invokes a method to email the report to DFO
  attemptTransmission(): void {
    // A reopened report still carries the old FOS error text; clear it before the pre-send save,
    // otherwise the saveTrip merge sees matching completionDates and restores the stale error
    // over the resubmission (ties keep the error).
    if (SharedMethodsService.getReportStatus(this.currTrip.startFishingReport.confirmationNumber) === 'Error with submission')
      this.currTrip.startFishingReport.confirmationNumber = 'Awaiting response';
    UserStorageService.saveUser(this.userDetails).then(() => {
      UserStorageService.saveTrip(this.currTrip).then(() => {
        // change false to !isProduction to enable mocking the submission
        if (false) { // mock the submission process if the app is in development mode
          this.sharedMethodsService.toastEmailSent();
          this.currTrip.startFishingReport.confirmationNumber = "FOS-12345";
          this.currTrip.startFishingReport.isSubmitted = true;
          UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'Trip', success: this.currTrip.startFishingReport.isSubmitted } }));
        } else {
          this.submissionService.sendReportData(this.userDetails.userEmail, this.startReport.reportID, this.currTrip.tripID).then((success: boolean) => {
            if (success) { // if the email was sent successfully
              this.sharedMethodsService.toastEmailSent();
              this.currTrip.startFishingReport.isSubmitted = true; // set the report submissionflag
              this.currTrip.startFishingReport.confirmationNumber = "Awaiting response";
              UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'Trip', success: this.currTrip.startFishingReport.isSubmitted } }));
            } else {
              this.currTrip.startFishingReport.confirmationNumber = "Not submitted";
              this.sharedMethodsService.toastUnableToSubmitReport();
              this.currTrip.unsubmittedReports.push(this.currTrip.startFishingReport.completionDate);
              UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'Trip', success: this.currTrip.startFishingReport.isSubmitted } }));
            }
            if (!this.isResubmission) { // only save the username to the trip lookup if it's the first time submitting the report
              this.sharedMethodsService.saveToLookupDictionary(this.userDetails.userEmail, this.currTrip.tripID).subscribe(() => {
                console.log("Saved username to trip lookup database");
              })
            }
          });
        }
        // navigate to the report submission page
      });
    })
  }

}
